import { useDesktopNotifications } from '../../hooks/useDesktopNotifications';

/**
 * Subtle text-button toggle for desktop alert opt-in. First click on an
 * un-prompted browser triggers Notification.requestPermission(); subsequent
 * clicks flip the user's local opt-in preference. Hidden entirely when the
 * platform doesn't support the Notification API.
 */
export function DesktopAlertsToggle() {
  const { permission, enabled, setEnabled, request } = useDesktopNotifications();

  if (permission === 'unsupported') return null;

  const handleClick = async () => {
    if (permission === 'default') {
      const res = await request();
      if (res === 'granted') setEnabled(true);
      return;
    }
    if (permission === 'denied') {
      // We can't programmatically re-prompt once denied. Keep the button
      // visible but informative.
      return;
    }
    setEnabled(!enabled);
  };

  const label = buildLabel(permission, enabled);
  const disabled = permission === 'denied';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={disabled ? 'Desktop alerts are blocked in your browser settings' : undefined}
      className={[
        'text-xs font-medium transition-colors',
        disabled
          ? 'cursor-not-allowed text-slate-400 dark:text-slate-600'
          : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
      ].join(' ')}
      aria-pressed={enabled}
    >
      {label}
    </button>
  );
}

function buildLabel(permission, enabled) {
  if (permission === 'denied') return 'Desktop alerts blocked';
  if (permission === 'default') return 'Enable desktop alerts';
  return enabled ? '✓ Desktop alerts on' : 'Enable desktop alerts';
}
